import { AxiosError } from "axios";
import { isAxiosError } from "axios";

import { reportUnexpectedApiErrorMessage } from "@services/sentry/captureException";

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === "object";
};

const parseBlobErrorPayload = async (value: unknown): Promise<unknown> => {
  if (!(value instanceof Blob)) {
    return value;
  }

  try {
    const text = await value.text();
    return JSON.parse(text);
  } catch {
    return value;
  }
};

const parseResponseErrorData = (value: unknown): string | null => {
  if (typeof value === "string") {
    // 500 errors in dev mode
    return value;
  }

  if (!isRecord(value)) {
    return null;
  }

  if (typeof value.error === "string") {
    // api/order-variants/:id/track 422 error
    return value.error;
  }

  if (typeof value.message === "string") {
    // stripe checkout error
    return value.message;
  }

  const arrayError = value.error ?? value.errors;

  if (Array.isArray(arrayError)) {
    const detailMessages = arrayError
      .map((entry) => {
        if (typeof entry === "string") {
          return entry;
        }
        if (isRecord(entry) && typeof entry.detail === "string") {
          return entry.detail.replace(/^base /i, "");
        }
        return null;
      })
      .filter((detail): detail is string => detail !== null);

    if (detailMessages.length > 0) {
      return detailMessages.join("\n");
    }
  }

  if (isRecord(value.errors) && typeof value.errors.detail === "string") {
    // usually 500 server errors
    return value.errors.detail;
  }

  return null;
};

export default async function parseApiErrorMessage(error: Error | AxiosError) {
  if (isAxiosError(error)) {
    if (error.code === "ETIMEDOUT") {
      return "The request took too long. This is most likely due to a slow internet connection. Please check your connection and try again.";
    }
    if (error.code === "ECONNABORTED") {
      return "Request aborted.";
    }

    const responseData = await parseBlobErrorPayload(error.response?.data);
    const parsedMessage = parseResponseErrorData(responseData);
    if (parsedMessage) {
      return parsedMessage;
    }

    reportUnexpectedApiErrorMessage(error);
  }
  return error.message;
}

export const withBetterErrorMessage = async (error: Error | AxiosError) => {
  error.message = await parseApiErrorMessage(error);
  return error;
};
